import { getBackendUrl } from '../config/api';
import React, { useState } from 'react';
import axios from 'axios';
import { Trash2, AlertTriangle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const DataCleanup = () => {
  const { toast } = useToast();
  const API = getBackendUrl() || '';
  
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleCleanupDuplicateEmployees = async () => {
    if (!window.confirm('⚠️ WARNING: This will remove duplicate employee records based on email. The first record for each email will be kept. Continue?')) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/employees/cleanup-duplicates`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResults(response.data);
      toast({
        title: 'Cleanup Completed',
        description: `Removed ${response.data.duplicates_removed || 0} duplicate employees`,
        variant: 'default'
      });
    } catch (error) {
      console.error('Error during cleanup:', error);
      toast({
        title: 'Cleanup Failed',
        description: error.response?.data?.detail || 'Failed to cleanup duplicate employees',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupOrphanedSessions = async () => {
    if (!window.confirm('⚠️ WARNING: This will remove sessions that reference deleted batches, subjects, teachers, or classrooms. These sessions cause fake conflicts. Continue?')) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/cleanup/orphaned-sessions`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResults(response.data);
      toast({
        title: 'Cleanup Completed',
        description: `Removed ${response.data.orphaned_sessions || 0} orphaned sessions`,
        variant: 'default'
      });
    } catch (error) {
      console.error('Error during cleanup:', error);
      toast({
        title: 'Cleanup Failed',
        description: error.response?.data?.detail || 'Failed to cleanup orphaned sessions',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupDuplicateTeachers = async () => {
    if (!window.confirm('⚠️ WARNING: This will remove duplicate teacher records based on email. The first record for each email will be kept. Continue?')) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/teachers/cleanup-duplicates`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResults(response.data);
      toast({
        title: 'Cleanup Completed',
        description: `Removed ${response.data.duplicates_removed || 0} duplicate teachers`,
        variant: 'default'
      });
    } catch (error) {
      console.error('Error during cleanup:', error);
      toast({
        title: 'Cleanup Failed',
        description: error.response?.data?.detail || 'Failed to cleanup duplicate teachers',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupOrphanedTeachers = async () => {
    if (!window.confirm('⚠️ CLEANUP ORPHANED TEACHERS:\n\nThis will deactivate teacher records where the associated employee is inactive or deleted.\n\nThis removes "old deleted" employees (like "old Anjali", "old Ruquiya") from dropdowns and filters.\n\nContinue?')) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/fix/cleanup-orphaned-teachers`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResults(response.data);
      toast({
        title: 'Orphaned Teachers Cleaned!',
        description: `Deactivated ${response.data.deactivated_count || 0} orphaned teacher records. They will no longer appear in dropdowns.`,
        variant: 'default'
      });
    } catch (error) {
      console.error('Error during cleanup:', error);
      toast({
        title: 'Cleanup Failed',
        description: error.response?.data?.detail || 'Failed to cleanup orphaned teachers',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewOrphanedTeachers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/fix/preview-orphaned-teachers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResults(response.data);
      
      const { would_deactivate_count, safe_active_count } = response.data;
      
      toast({
        title: 'Preview Results',
        description: `${would_deactivate_count} orphaned teachers would be deactivated. ${safe_active_count} active teachers are safe.`,
        variant: 'default'
      });
      
      // Show detailed results
      if (response.data.would_deactivate && response.data.would_deactivate.length > 0) {
        console.log('Teachers that would be deactivated:', response.data.would_deactivate);
      }
    } catch (error) {
      console.error('Error during preview:', error);
      toast({
        title: 'Preview Failed',
        description: error.response?.data?.detail || 'Failed to preview cleanup',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFixMBABatchFields = async () => {
    if (!window.confirm('⚠️ FIX MBA BATCH FIELDS: This will update MBA batch stream and semester fields to match MBA subjects.\n\nChanges:\n- Stream: "MBA" → "MBA Design Management"\n- Semester: "Semester 1, Semester 2" → "PG Sem 1, PG Sem 2"\n- Year: "FY" → "PG FY", "SY" → "PG SY"\n\nThis will fix MBA subject mapping in Smart Builder. Continue?')) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/fix/mba-batch-fields`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResults(response.data);
      toast({
        title: 'MBA Batch Fields Updated!',
        description: `Updated ${response.data.updated_count || 0} MBA batches. MBA subjects should now appear in Smart Builder!`,
        variant: 'default'
      });
    } catch (error) {
      console.error('Error during MBA fix:', error);
      toast({
        title: 'MBA Fix Failed',
        description: error.response?.data?.detail || 'Failed to fix MBA batch fields',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupInactiveStaff = async () => {
    if (!window.confirm('⚠️ PERMANENT DELETION WARNING!\n\nThis will PERMANENTLY DELETE all inactive staff members from the database:\n- Employee records\n- User accounts\n- Teacher records\n\nThis action CANNOT be undone!\n\nAre you absolutely sure?')) {
      return;
    }
    
    if (!window.confirm('⚠️ FINAL CONFIRMATION\n\nThis is your last chance to cancel.\n\nClick OK to permanently delete all inactive staff members.')) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/staff/cleanup-inactive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResults(response.data);
      toast({
        title: 'Inactive Staff Deleted!',
        description: `Permanently deleted ${response.data.deleted_count || 0} inactive staff members from database.`,
        variant: 'default'
      });
    } catch (error) {
      console.error('Error during cleanup:', error);
      toast({
        title: 'Cleanup Failed',
        description: error.response?.data?.detail || 'Failed to cleanup inactive staff',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFixAllPGYearFields = async () => {
    if (!window.confirm('⚠️ FIX ALL PG BATCH YEAR FIELDS: This will update year fields for ALL PG batches (MBA, Fashion, Interior).\n\nChanges:\n- Year: "FY" → "PG FY"\n- Year: "SY" → "PG SY"\n\nThis will fix PG subject mapping across all streams. Continue?')) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/fix/all-pg-batch-year-fields`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResults(response.data);
      toast({
        title: 'All PG Year Fields Updated!',
        description: `Updated ${response.data.updated_count || 0} PG batches. All PG subjects should now appear in Smart Builder!`,
        variant: 'default'
      });
    } catch (error) {
      console.error('Error during PG year fix:', error);
      toast({
        title: 'PG Year Fix Failed',
        description: error.response?.data?.detail || 'Failed to fix PG year fields',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAllUsers = async () => {
    if (!window.confirm('⚠️ MERGE ALL USERS TO EMPLOYEES:\n\nThis will:\n- Create employee records for all users who don\'t have one\n- Activate all inactive employee records\n- Merge old "Register as Teacher" users into Employee Management\n\nThis is a BULK operation that affects all users in the system.\n\nContinue?')) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/fix/sync-all-users-to-employees`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResults(response.data);
      const report = response.data.report;
      toast({
        title: 'Bulk Sync Completed!',
        description: `Created: ${report.employees_created}, Activated: ${report.employees_activated}, Already Active: ${report.already_active}`,
        variant: 'default'
      });
    } catch (error) {
      console.error('Error during bulk sync:', error);
      toast({
        title: 'Bulk Sync Failed',
        description: error.response?.data?.detail || 'Failed to sync all users',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleActivateEmployee = async () => {
    const email = window.prompt('Enter the email address of the employee to activate:\n\nExample: anjali.nifpcmc@gmail.com\n\nThis will make inactive employees visible in the employees list.');
    
    if (!email) return;
    
    if (!window.confirm(`⚠️ ACTIVATE EMPLOYEE:\n\nThis will activate the employee record for: ${email}\n\nThis makes hidden/inactive employees visible and functional.\n\nContinue?`)) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/fix/activate-employee?email=${encodeURIComponent(email)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResults(response.data);
      if (response.data.success) {
        const wasActive = response.data.was_already_active;
        toast({
          title: wasActive ? 'Already Active' : 'Employee Activated!',
          description: wasActive 
            ? `${email} was already active.`
            : `Activated employee record for ${email}. They can now access all features.`,
          variant: 'default'
        });
      }
    } catch (error) {
      console.error('Error during activation:', error);
      toast({
        title: 'Activation Failed',
        description: error.response?.data?.detail || 'Failed to activate employee',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRevertPGYears = async () => {
    if (!window.confirm('⚠️ REVERT MBA BATCH YEARS TO LEGACY FORMAT:\n\nThis will change MBA batch years back to match your production subjects:\n- "PG FY" → "Year 1"\n- "PG SY" → "Year 2"\n\nUse this if your MBA subjects still have Year 1/Year 2 format. Continue?')) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/fix/revert-pg-batch-years-to-legacy`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResults(response.data);
      toast({
        title: 'MBA Years Reverted to Legacy!',
        description: `Updated ${response.data.updated_count || 0} MBA batches to use Year 1/Year 2 format.`,
        variant: 'default'
      });
    } catch (error) {
      console.error('Error during revert:', error);
      toast({
        title: 'Revert Failed',
        description: error.response?.data?.detail || 'Failed to revert year fields',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Trash2 className="w-8 h-8 mr-3 text-red-600" />
            Data Cleanup Tools
          </h1>
          <p className="text-gray-600 mt-2">Admin-only tools for database maintenance</p>
        </div>
      </div>

      {/* Warning Banner */}
      <Card className="bg-yellow-50 border-yellow-200">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-yellow-900">Important Warning</h3>
              <p className="text-sm text-yellow-800 mt-1">
                These cleanup operations will permanently delete data from the database. 
                Always confirm what you're cleaning up before proceeding. These actions cannot be undone.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Merge Section */}
      <Card className="bg-indigo-50 border-indigo-200 mb-6">
        <CardHeader>
          <CardTitle className="text-xl text-indigo-900 flex items-center">
            <AlertTriangle className="w-6 h-6 mr-2" />
            Merge All Users to Employees (One-Time Setup)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-700 mb-4">
            <strong>Recommended for first-time setup:</strong> This merges ALL users from the old "Register as Teacher" system into Employee Management. 
            Creates/activates employee records for all approved users. Run this once to eliminate confusion between User Management and Employee Management.
          </p>
          <Button 
            onClick={handleSyncAllUsers}
            disabled={loading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
          >
            {loading ? 'Processing...' : 'Merge All Users to Employees'}
          </Button>
        </CardContent>
      </Card>

      {/* Cleanup Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Duplicate Employees */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Duplicate Employees</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Removes duplicate employee records with the same email. Keeps the first record for each email.
            </p>
            <Button 
              onClick={handleCleanupDuplicateEmployees}
              disabled={loading}
              variant="destructive"
              className="w-full"
            >
              {loading ? 'Processing...' : 'Cleanup Employees'}
            </Button>
          </CardContent>
        </Card>

        {/* Duplicate Teachers */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Duplicate Teachers</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Removes duplicate teacher records with the same email. Keeps the first record for each email.
            </p>
            <Button 
              onClick={handleCleanupDuplicateTeachers}
              disabled={loading}
              variant="destructive"
              className="w-full"
            >
              {loading ? 'Processing...' : 'Cleanup Teachers'}
            </Button>
          </CardContent>


        {/* Orphaned Teachers */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Orphaned Teachers</CardTitle>
          </CardHeader>


        {/* Cleanup Inactive Staff - SUPER ADMIN ONLY */}
        <Card className="border-red-500">
          <CardHeader>
            <CardTitle className="text-lg text-red-600">⚠️ Cleanup Inactive Staff (PERMANENT)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              <strong className="text-red-600">WARNING:</strong> Permanently deletes all inactive staff members from database. This includes old/deleted employees, test data, and duplicates. Cannot be undone!
            </p>
            <Button 
              onClick={handleCleanupInactiveStaff}
              disabled={loading}
              variant="destructive"
              className="w-full"
            >
              {loading ? 'Processing...' : 'Permanently Delete Inactive Staff'}
            </Button>
          </CardContent>
        </Card>

          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Deactivates teacher records where the associated employee is deleted/inactive. Removes "old deleted" employees from dropdowns.
            </p>
            <div className="space-y-2">
              <Button 
                onClick={handlePreviewOrphanedTeachers}
                disabled={loading}
                variant="outline"
                className="w-full"
              >
                {loading ? 'Loading...' : 'Preview Cleanup (Safe)'}
              </Button>
              <Button 
                onClick={handleCleanupOrphanedTeachers}
                disabled={loading}
                variant="destructive"
                className="w-full"
              >
                {loading ? 'Processing...' : 'Run Cleanup'}
              </Button>
            </div>
          </CardContent>
        </Card>

        </Card>

        {/* Orphaned Sessions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Orphaned Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Removes sessions referencing deleted batches, subjects, teachers, or classrooms (fake conflicts).
            </p>
            <Button 
              onClick={handleCleanupOrphanedSessions}
              disabled={loading}
              variant="destructive"
              className="w-full"
            >
              {loading ? 'Processing...' : 'Cleanup Sessions'}
            </Button>
          </CardContent>
        </Card>

        {/* Fix MBA Batch Fields */}
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg text-blue-900">Fix MBA Batch Fields</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700 mb-4">
              Updates MBA batch stream, year, and semester fields to match subject conventions.
            </p>
            <Button 
              onClick={handleFixMBABatchFields}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Processing...' : 'Fix MBA Batches'}
            </Button>
          </CardContent>
        </Card>

        {/* Fix All PG Year Fields */}
        <Card className="bg-purple-50 border-purple-200">
          <CardHeader>
            <CardTitle className="text-lg text-purple-900">Fix ALL PG Year Fields</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700 mb-4">
              Updates year fields for ALL PG batches (MBA, Fashion, Interior). Changes "FY" → "PG FY", "SY" → "PG SY".
            </p>
            <Button 
              onClick={handleFixAllPGYearFields}
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {loading ? 'Processing...' : 'Fix All PG Years'}
            </Button>
          </CardContent>
        </Card>

        {/* Revert MBA Years to Legacy */}
        <Card className="bg-orange-50 border-orange-200">
          <CardHeader>
            <CardTitle className="text-lg text-orange-900">Revert MBA Years (Production Fix)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700 mb-4">
              For production: Reverts MBA batch years to match legacy subject format. "PG FY" → "Year 1", "PG SY" → "Year 2".
            </p>
            <Button 
              onClick={handleRevertPGYears}
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700"
            >
              {loading ? 'Processing...' : 'Revert to Legacy'}
            </Button>
          </CardContent>
        </Card>

        {/* Activate Employee */}
        <Card className="bg-green-50 border-green-200">
          <CardHeader>
            <CardTitle className="text-lg text-green-900">Activate Employee</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700 mb-4">
              Activates inactive employee records to make them visible and functional (fixes Anjali Badure issue).
            </p>
            <Button 
              onClick={handleActivateEmployee}
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {loading ? 'Processing...' : 'Activate Employee'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Results Display */}
      {results && (
        <Card className="bg-green-50 border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center text-green-900">
              <CheckCircle className="w-5 h-5 mr-2" />
              Cleanup Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-sm bg-white p-4 rounded border overflow-auto max-h-96">
              {JSON.stringify(results, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DataCleanup;
